import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  useScrollTrigger,
  Slide,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Home as HomeIcon,
  PersonAdd,
  Groups,
  HowToVote,
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Toll,
  ManageAccounts,
  MenuBook,
  Menu as MenuIcon,
  Close as CloseIcon,
  SportsEsports,
  Storefront,
  People,
  OpenInNew
} from '@mui/icons-material';
import '../styles/navbar.css';

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadUser = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  };

  // Sync les ogrines ET les votes depuis le serveur (source de verite)
  const syncOgrines = useCallback(async () => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('sessionToken');
    if (!stored || !token) return;
    
    const u = JSON.parse(stored);
    if (!u?.id) return;
    
    try {
      const res = await fetch(`${API_URL}/account/profile/${u.id}?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store'
      });
      if (res.ok) {
        const profile = await res.json();
        let hasChanges = false;

        if (profile.ogrines !== undefined && profile.ogrines !== u.ogrines) {
          u.ogrines = profile.ogrines;
          hasChanges = true;
        }

        // Vérification des votes
        const profileVotes = profile.votes !== undefined ? profile.votes : profile.monthly_votes;
        if (profileVotes !== undefined && profileVotes !== u.votes) {
          u.votes = profileVotes;
          hasChanges = true;
        }

        if (hasChanges) {
          localStorage.setItem('user', JSON.stringify(u));
          setUser({ ...u });
        }
      }
    } catch (_) { /* silencieux */ }
  }, []);

  useEffect(() => {
    loadUser();
    syncOgrines();
    window.addEventListener('storage', loadUser);
    
    const interval = setInterval(syncOgrines, 10000);
    
    const onFocus = () => syncOgrines();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncOgrines();
    });
    window.addEventListener('focus', onFocus);
    
    return () => {
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [syncOgrines]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('sessionToken');
    if (token) {
      try {
        await fetch(`${API_URL}/account/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (_) { /* silencieux - la deco locale se fait quoi qu'il arrive */ }
    }
    localStorage.removeItem('user');
    localStorage.removeItem('sessionToken');
    setUser(null);
    handleMenuClose();
    setDrawerOpen(false);
    navigate('/');
  };

  // Condition dynamique pour n'afficher la boutique que si l'utilisateur a au moins 1 vote
  const navItems = [
    { path: '/',    label: 'Accueil',  icon: <HomeIcon /> },
    ...(user && user.votes >= 1 ? [{ path: '/shop', label: 'Boutique', icon: <Storefront /> }] : []),
    { path: '/players', label: 'Top Votes', icon: <HowToVote /> },
    { path: 'https://wiki.nexus-temporel.com', label: 'Wiki', icon: <MenuBook />, isExternal: true },
    { path: '/wiki',    label: 'Classes',   icon: <People /> },
    ...(user ? [{ path: '/games', label: 'Jeux', icon: <SportsEsports /> }] : []),
    { path: '/vote',    label: 'Voter',     icon: <HowToVote /> },
    { path: '/join-us', label: 'Rejoindre', icon: <Groups /> },
  ];

  // Drawer mobile
  const drawerContent = (
    <Box
      sx={{
        width: 270,
        height: '100%',
        background: 'rgba(10, 4, 26, 0.98)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        flexDirection: 'column',
        pt: 1,
      }}
    >
      {/* En-tete drawer */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box component={Link} to="/" onClick={() => setDrawerOpen(false)} sx={{ lineHeight: 0, textDecoration: 'none' }}>
          <img
            src="/assets/images/Nexus.png"
            alt="Nexus"
            style={{ height: '40px', width: 'auto', filter: 'drop-shadow(0 0 10px rgba(182,122,60,0.8))' }}
          />
        </Box>
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(182,122,60,0.2)', mb: 1 }} />

      {/* Liens de navigation */}
      <List sx={{ flex: 1, px: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path && !item.isExternal;
          return (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={item.isExternal ? 'a' : Link}
                to={item.isExternal ? undefined : item.path}
                href={item.isExternal ? item.path : undefined}
                target={item.isExternal ? "_blank" : undefined}
                rel={item.isExternal ? "noopener noreferrer" : undefined}
                onClick={() => !item.isExternal && setDrawerOpen(false)}
                sx={{
                  borderRadius: '12px',
                  background: isActive ? 'rgba(182,122,60,0.2)' : 'transparent',
                  border: isActive ? '1px solid rgba(182,122,60,0.4)' : '1px solid transparent',
                  '&:hover': { background: 'rgba(182,122,60,0.12)', border: '1px solid rgba(182,122,60,0.25)' },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? '#d5a777' : 'rgba(255,255,255,0.5)', minWidth: 38 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 400, color: isActive ? '#fff' : 'rgba(255,255,255,0.75)', fontSize: '0.95rem' }}
                />
                {item.isExternal && <OpenInNew sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(182,122,60,0.2)' }} />

      {/* Section compte en bas du drawer */}
      {user ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Avatar sx={{ width: 38, height: 38, background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)', fontSize: '1rem' }}>
              {user.username.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700} color="#fff">{user.username}</Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.45)">{user.email}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1 }}>
            <Toll sx={{ color: '#C9CED6', fontSize: 18 }} />
            <Typography variant="body2" color="rgba(255,255,255,0.8)">{user.ogrines || 0} <img src="/assets/images/1.png" alt="og" className="og-icon" /></Typography>
          </Box>
          <ListItemButton
            component={Link} to="/account"
            onClick={() => setDrawerOpen(false)}
            sx={{ borderRadius: '10px', mb: 0.5, '&:hover': { background: 'rgba(182,122,60,0.12)' } }}
          >
            <ListItemIcon sx={{ color: '#d5a777', minWidth: 38 }}><ManageAccounts /></ListItemIcon>
            <ListItemText primary="Mon compte" primaryTypographyProps={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }} />
          </ListItemButton>
          <ListItemButton
            onClick={handleLogout}
            sx={{ borderRadius: '10px', '&:hover': { background: 'rgba(244,67,54,0.1)' } }}
          >
            <ListItemIcon sx={{ color: '#ef9a9a', minWidth: 38 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Se deconnecter" primaryTypographyProps={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }} />
          </ListItemButton>
        </Box>
      ) : (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            component={Link} to="/login"
            onClick={() => setDrawerOpen(false)}
            startIcon={<LoginIcon />}
            fullWidth
            sx={{
              color: 'rgba(255,255,255,0.8)', borderRadius: '50px', border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.35)' }
            }}
          >
            Se connecter
          </Button>
          <Button
            component={Link} to="/signup"
            onClick={() => setDrawerOpen(false)}
            variant="contained" startIcon={<PersonAdd />} fullWidth
            sx={{
              background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)', borderRadius: '50px', fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)' }
            }}
          >
            S'inscrire
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <HideOnScroll>
      <AppBar
        position="fixed"
        className="navbar-modern"
        sx={{
          background: 'rgba(8, 4, 20, 0.55)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 1px 0 rgba(182, 122, 60, 0.2), 0 8px 40px rgba(0,0,0,0.4)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '64px !important', py: 0.5 }}>

            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{ textDecoration: 'none', lineHeight: 0, flexShrink: 0 }}
            >
              <img
                src="/assets/images/Nexus.png"
                alt="Nexus"
                style={{ height: '46px', width: 'auto', filter: 'drop-shadow(0 0 14px rgba(182, 122, 60, 0.8))' }}
              />
            </Box>

            {/* Desktop: liens horizontaux */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path && !item.isExternal;
                return (
                  <Button
                    key={item.label}
                    component={item.isExternal ? 'a' : Link}
                    to={item.isExternal ? undefined : item.path}
                    href={item.isExternal ? item.path : undefined}
                    target={item.isExternal ? "_blank" : undefined}
                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                    startIcon={item.icon}
                    endIcon={item.isExternal ? <OpenInNew sx={{ fontSize: '14px !important', opacity: 0.5 }} /> : null}
                    sx={{
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                      fontWeight: isActive ? 700 : 400,
                      borderRadius: '50px',
                      padding: '7px 18px',
                      fontSize: '0.875rem',
                      letterSpacing: '0.02em',
                      background: isActive ? 'rgba(182, 122, 60, 0.22)' : 'transparent',
                      border: isActive ? '1px solid rgba(182, 122, 60, 0.45)' : '1px solid transparent',
                      transition: 'all 0.25s ease',
                      '& .MuiButton-startIcon': { opacity: isActive ? 1 : 0.6 },
                      '&:hover': {
                        color: '#fff',
                        background: 'rgba(182, 122, 60, 0.15)',
                        border: '1px solid rgba(182, 122, 60, 0.3)',
                        '& .MuiButton-startIcon': { opacity: 1 },
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}

              {user ? (
                <>
                  <Button
                    onClick={handleMenuOpen}
                    startIcon={<AccountCircle />}
                    sx={{
                      color: '#fff', fontWeight: 600, marginLeft: 1.5, padding: '7px 18px',
                      borderRadius: '50px', background: 'rgba(182, 122, 60, 0.2)',
                      border: '1px solid rgba(182, 122, 60, 0.45)', fontSize: '0.875rem',
                      '&:hover': { background: 'rgba(182, 122, 60, 0.32)', border: '1px solid rgba(182, 122, 60, 0.7)' }
                    }}
                  >
                    {user.username}
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                      sx: {
                        background: 'rgba(26, 10, 46, 0.95)', backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(182, 122, 60, 0.3)', marginTop: 1, minWidth: 220
                      }
                    }}
                  >
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)' }}>
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{user.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <Divider sx={{ borderColor: 'rgba(182, 122, 60, 0.2)' }} />
                    <MenuItem>
                      <Toll sx={{ mr: 1, color: '#C9CED6' }} />
                      <Typography variant="body2">{user.ogrines || 0} <img src="/assets/images/1.png" alt="og" className="og-icon" /></Typography>
                    </MenuItem>
                    <Divider sx={{ borderColor: 'rgba(182, 122, 60, 0.2)' }} />
                    <MenuItem component={Link} to="/account" onClick={handleMenuClose}>
                      <ManageAccounts sx={{ mr: 1, color: '#B67A3C' }} />
                      Mon compte
                    </MenuItem>
                    <Divider sx={{ borderColor: 'rgba(182, 122, 60, 0.2)' }} />
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} />
                      Se deconnecter
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1.5 }}>
                  <Button
                    component={Link} to="/login" startIcon={<LoginIcon />}
                    sx={{
                      color: 'rgba(255,255,255,0.75)', fontWeight: 500,
                      padding: '6px 18px', borderRadius: '50px', fontSize: '0.875rem',
                      border: '1px solid rgba(255,255,255,0.15)',
                      '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.3)' }
                    }}
                  >
                    Se connecter
                  </Button>
                  <Button
                    component={Link} to="/signup" variant="contained" startIcon={<PersonAdd />}
                    sx={{
                      background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)',
                      color: '#ffffff', fontWeight: 700, padding: '6px 20px',
                      borderRadius: '50px', fontSize: '0.875rem',
                      boxShadow: '0 0 16px rgba(182, 122, 60, 0.35)',
                      '&:hover': { background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)', transform: 'translateY(-1px)', boxShadow: '0 0 24px rgba(182, 122, 60, 0.55)' }
                    }}
                  >
                    S'inscrire
                  </Button>
                </Box>
              )}
            </Box>

            {/* Mobile: bouton hamburger */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
              {user && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Toll sx={{ color: '#C9CED6', fontSize: 16 }} />
                  <Typography variant="caption" color="rgba(255,255,255,0.8)" fontWeight={600}>
                    {user.ogrines || 0}
                  </Typography>
                </Box>
              )}
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: '#fff',
                  border: '1px solid rgba(182,122,60,0.4)',
                  borderRadius: '10px',
                  p: '7px',
                  '&:hover': { background: 'rgba(182,122,60,0.15)' }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

          </Toolbar>
        </Container>

        {/* Drawer slide-in depuis la droite */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
        >
          {drawerContent}
        </Drawer>

      </AppBar>
    </HideOnScroll>
  );
};

export default Navbar;